import React, { FC, useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { OrganizationItemStore, OrganizationSummary, OrganizationSummaryStore } from 'src/stores/organizations-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Button, Card, Row, Spin, Tag } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import NewOrganizationDialog from './organization-new';
import DeleteOrganizationConfirmationModal from './organization-delete';
import PartnerInfo from 'src/common/partner-info';
import { CommandResult } from 'src/core/stores/types';
import { App } from 'antd';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { Country, PermissionsConstants } from 'src/common/types';
import { useIdentity } from 'src/core/services/authentication';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { ENV } from 'src/';
import { emptyTableModel } from 'src/common/utils';
import { useNavigate, Link } from 'react-router-dom';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import { AppConfiguration } from 'src/core/services/configuration.service';

import { theme } from 'antd';
const { useToken } = theme;

const permissions = [
  PermissionsConstants.CreateOrganization,
  PermissionsConstants.DeleteOrganization,
  PermissionsConstants.ManageOrganizationInfo,
  PermissionsConstants.BlockOrganization,
  PermissionsConstants.ManagePartnerOrganization,
];

const OrganizationsListPage: FC = () => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config: AppConfiguration = useContext(AppConfig);
  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'displayName', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const organizationsStore = container.get(OrganizationSummaryStore);
  const organizationsState = organizationsStore.state;
  const organizationItemStore = container.get(OrganizationItemStore);

  const partnerInfo = container.get<PartnerInfo>(PartnerInfo);

  const [newOrganizationVisible, setNewOrganizationVisible] = useState(false);
  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [toDeleteOrg, setToDeleteOrg] = useState<OrganizationSummary>();
  const [countryOptions, setCountryOptions] = useState<string[]>([]);
  const { modal } = App.useApp();
  const { token } = useToken();
  const bgColor = token.colorBgContainer;
  setHeader(t('Organizations'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'organizations', breadcrumbName: t('Organizations') },
  ]);

  useEffect(
    () => {
      const load = async () => {
        await organizationsStore.load({ take: 100000, skip: 0 });
        setCountryOptions(organizationsState.items.value.flatMap((v) => (v.country ? [v.country] : [])));
        await organizationsStore.load(query);
      };
      load();
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (checkingPermissions) {
    return (
      <Row align="top" justify="space-between">
        <Spin spinning size="large" style={{ maxHeight: '75vh' }}>
          <Card style={{ width: '80vw', height: '75vh', backgroundColor: bgColor }}></Card>
        </Spin>
      </Row>
    );
  }

  const canCreateOrg = userPermissionsSet.has(PermissionsConstants.CreateOrganization);
  const canDeleteOrg = userPermissionsSet.has(PermissionsConstants.DeleteOrganization);
  const canEditOrg = userPermissionsSet.has(PermissionsConstants.ManageOrganizationInfo);
  const canBlockOrg = userPermissionsSet.has(PermissionsConstants.BlockOrganization);
  const canManagePartnerOrg = userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization);

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        searchable: true,
        field: 'DisplayName',
        title: t('DisplayName'),
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={organizationsStore.baseUrl} fieldName={'DisplayName'} fieldValue={'displayName'} />,
        fiterField: 'DisplayName',
        renderer: (data) => (
          <span>
            <Link to={{ pathname: `${data.id}` }} state={{ canEditOrg, canDeleteOrg, canBlockOrg, canCreateOrg }}>
              <span>{data.displayName}</span>
            </Link>
          </span>
        ),
      },
      {
        sortable: true,
        searchable: true,
        field: 'Email',
        title: t('Email'),
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={organizationsStore.baseUrl} fieldName={'Email'} fieldValue={'email'} />,
        fiterField: 'Email',
        renderer: (data) => <span>{data.email}</span>,
      },
      {
        sortable: false,
        searchable: true,
        field: 'Address',
        title: t('Address'),
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={organizationsStore.baseUrl} fieldName={'Address'} fieldValue={'address'} />,
        fiterField: 'Address',
        renderer: (data) => <span>{data.address}</span>,
      },
      {
        sortable: false,
        field: 'Country',
        title: t('Country'),
        filter: (
          <OptionsFilter
            allowSearch
            options={Object.values(Country)
              .map((country) => ({ label: t(country), value: country }))
              .filter((v) => countryOptions.includes(v.value))}
          />
        ),
        fiterField: 'Country',
        renderer: (data) => <span>{t(data.country)}</span>,
      },
      {
        sortable: false,
        searchable: false,
        field: 'parentOrganizationDisplayName',
        title: t('PartnerOrganization'),
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter
            baseUrl={organizationsStore.baseUrl}
            fieldName={'ParentOrganization/DisplayName'}
            fieldValue={'parentOrganizationDisplayName'}
          />
        ),
        fiterField: 'ParentOrganization/DisplayName',
        renderer: (data) => <span>{data.parentOrganizationDisplayName ? <Tag>{data.parentOrganizationDisplayName}</Tag> : '-'}</span>,
      },
      {
        sortable: false,
        searchable: false,
        title: '',
        renderer: (data: OrganizationSummary) => {
          if (!canCreateOrg) return null;
          if (data.id === identity.profile['tenant_id']) return null;
          return (
            <span className="clickable-span strong" onClick={() => onDeleteRow(data)}>
              {t('Delete')}
            </span>
          );
        },
      },
    ],
    data: organizationsState.value,
  } as TableModel<OrganizationSummary>;

  const onDeleteRow = async (item: OrganizationSummary) => {
    const isExporting = await organizationItemStore.checkExportingStatus(item.id);
    if (isExporting) {
      modal.error({
        title: t('CanNotDeleteOrganizationWhileExporting').replace('{0}', item.displayName),
        okText: t('OK'),
        cancelText: t('Cancel'),
      });
      return Promise.resolve<CommandResult<any>>({ isSuccess: false, error: t('CanNotDeleteOrganizationWhileExporting') } as CommandResult<any>);
    }

    if (!canDeleteOrg) {
      modal.error({
        title: t('CanNotDeleteOrgs'),
        okText: t('OK'),
        cancelText: t('Cancel'),
      });
      return Promise.resolve<CommandResult<any>>({ isSuccess: false, error: t('CanNotDeleteOrgs') } as CommandResult<any>);
    } else if (item.id === partnerInfo.organizationId) {
      modal.error({
        title: t('CanNotdeleteOwnOrganization'),
        okText: t('OK'),
        cancelText: t('Cancel'),
      });
      return Promise.resolve<CommandResult<any>>({ isSuccess: false, error: t('CanNotdeleteOwnOrganization') } as CommandResult<any>);
    } else {
      setToDeleteOrg(item);
      setShowDeleteConfirmationModal(true);
      return Promise.resolve<CommandResult<any>>({ isSuccess: true } as CommandResult<any>);
    }
  };

  const onDeleteCancel = () => {
    setToDeleteOrg(undefined);
    setShowDeleteConfirmationModal(false);
  };

  const onDeleteConfirm = async () => {
    if (toDeleteOrg == null) return;
    setShowDeleteConfirmationModal(false);
    await organizationsStore.delete(toDeleteOrg.id);
    setToDeleteOrg(undefined);
    await organizationsStore.load(query);
  };

  return (
    <Row align="top" justify="space-between">
      <Card style={{ width: '100%' }} className="reorder-buttons">
        <TableView
          rowKey="id"
          canCreateNew={canCreateOrg}
          onNewItem={() => setNewOrganizationVisible(true)}
          newItemName={t('AddOrganization')}
          canDelete={false}
          onDeleteRow={onDeleteRow}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            organizationsStore.load(query);
          }}
          onRefresh={() => organizationsStore.load(query)}
          model={organizationsState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            organizationsState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(organizationsState.errorMessage.value) : t('OrganizationsTableError'))
          }
          rightToolbar={
            <>
              {canManagePartnerOrg && (
                <li className="table-buttons-list-element" style={{ marginLeft: '10px', marginRight: 0 }}>
                  <Button type="primary" onClick={() => navigate(`backups`)}>
                    {t('Backups')}
                  </Button>
                </li>
              )}
            </>
          }
        />
        {canCreateOrg && newOrganizationVisible && (
          <NewOrganizationDialog
            onClose={(response: any) => {
              setNewOrganizationVisible(false);
              if (response) {
                if (response.identity) navigate(`${response.identity}`, { state: { canEditOrg, canDeleteOrg, canBlockOrg, canCreateOrg } });
                else organizationsStore.load(query);
              }
            }}
          />
        )}
        {canDeleteOrg && showDeleteConfirmationModal && toDeleteOrg && (
          <DeleteOrganizationConfirmationModal onClose={onDeleteCancel} onOk={onDeleteConfirm} organizationName={toDeleteOrg.displayName} />
        )}
      </Card>
    </Row>
  );
};

export default OrganizationsListPage;
